import { Box, Drawer, IconButton, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { makeStyles } from 'tss-react/mui';
import { useState } from 'react';
import { useL10n } from '@uplift/hooks';

import {
  AgeGateHandsHeartIcon, HeartHandIcon, HeartOutlinePurpleIcon, SearchIcon,
  ShieldChildhelpIcon, StarOutlineBlueIcon, StarOutlineGreenIcon,
} from '@uplift/icons';

import {
  StepContainer, StepContent, StepDescription, StepHeading, StepNavigator
} from '../../components';

const findCareLink = 'https://www.teencounseling.com/?utm_source=bhpsych';
const call988LifeLineLink = 'https://988lifeline.org/get-help/';
const namiHelpLineLink = 'https://www.nami.org/nami-helpline/teen-young-adult-helpline/';
const trevorProjectLink = 'https://www.thetrevorproject.org/get-help/';
const childHelpHotlineLink = 'https://childhelphotline.org/teens/';


const LearnMoreLink: React.VFC<{ href: string }> = ({ href }) => {
  const { classes } = useTooYoungStyles();
  const l10n = useL10n();

  return (
    <a href={href} target="_blank" rel="noreferrer" className={classes.learnMoreLink}>
      <Typography variant="body2" color="primary" fontWeight={600}>{l10n.navigation.learnMore}</Typography>
      <OpenInNewIcon fontSize="inherit" color="primary" />
    </a>
  );
};

export type TooYoungProps = {
  onBack: () => void;
  title?: string;
  description?: string;
};

export const TooYoung: React.VFC<TooYoungProps> = props => {
  const { classes } = useTooYoungStyles();
  const l10n = useL10n();
  const [ drawerOpen, setDrawerOpen ] = useState(false);

  return (
    <StepContainer>
      <StepContent className={classes.content}>
        <Box className={classes.header}>
          <Box className={classes.iconCircle}>
            <AgeGateHandsHeartIcon className={classes.headerIcon} />
          </Box>
          <Box>
            <StepHeading>{props.title ?? l10n.onboarding.tooYoung.title}</StepHeading>
            <StepDescription>{props.description ?? l10n.onboarding.tooYoung.description}</StepDescription>
          </Box>
        </Box>
        <Box className={classes.resourceList}>
          <a href={findCareLink} target="_blank" rel="noreferrer" className={classes.resourceRowLink}>
            <Box className={classes.resourceRow}>
              <Box className={classes.iconCircle}><SearchIcon className={classes.rowIcon} /></Box>
              <Box className={classes.resourceText}>
                <Typography variant="body1" fontWeight={600}>{l10n.onboarding.tooYoung.findCare.title}</Typography>
                <Typography variant="body2" color="text.secondary">{l10n.onboarding.tooYoung.findCare.description}</Typography>
              </Box>
              <ArrowForwardIcon fontSize="small" color="primary" />
            </Box>
          </a>
          <a href={call988LifeLineLink} target="_blank" rel="noreferrer" className={classes.resourceRowLink}>
            <Box className={classes.resourceRow}>
              <Box className={classes.iconCircle}><HeartHandIcon className={classes.rowIcon} /></Box>
              <Box className={classes.resourceText}>
                <Typography variant="body1" fontWeight={600}>{l10n.onboarding.tooYoung.needHelp.title}</Typography>
                <Typography variant="body2" color="text.secondary">{l10n.onboarding.tooYoung.needHelp.description}</Typography>
              </Box>
              <Box className={classes.resourceAction}>
                <Typography variant="body2" color="primary" fontWeight={600}>{l10n.onboarding.tooYoung.needHelp.action}</Typography>
                <ArrowForwardIcon fontSize="small" color="primary" />
              </Box>
            </Box>
          </a>
          <Box
            className={classes.resourceRow}
            onClick={() => setDrawerOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' || e.key === ' ' ? setDrawerOpen(true) : undefined}
          >
            <Box className={classes.iconCircle}><StarOutlineGreenIcon className={classes.rowIcon} /></Box>
            <Box className={classes.resourceText}>
              <Typography variant="body1" fontWeight={600}>{l10n.onboarding.tooYoung.additionalSupport.title}</Typography>
              <Typography variant="body2" color="text.secondary">{l10n.onboarding.tooYoung.additionalSupport.description}</Typography>
            </Box>
            <Box className={classes.resourceAction}>
              <Typography variant="body2" color="primary" fontWeight={600}>{l10n.onboarding.tooYoung.additionalSupport.action}</Typography>
              <ArrowForwardIcon fontSize="small" color="primary" />
            </Box>
          </Box>
        </Box>
      </StepContent>
      <StepNavigator onBack={props.onBack} desktopOnly />
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        slotProps={{ paper: { className: classes.drawerPaper } }}
      >
        <Box className={classes.drawerContent}>
          <Box className={classes.drawerHeader}>
            <Typography variant="h6" fontWeight={600}>{l10n.onboarding.tooYoung.additionalSupport.drawerTitle}</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Box className={classes.drawerItem}>
            <HeartOutlinePurpleIcon className={classes.drawerItemIcon} />
            <Box className={classes.drawerItemContent}>
              <Typography variant="body1" fontWeight={700}>{l10n.onboarding.tooYoung.nami.title}</Typography>
              <Typography variant="body2">{l10n.onboarding.tooYoung.nami.description}</Typography>
              <Typography variant="body2">{l10n.onboarding.tooYoung.nami.availability}</Typography>
              <LearnMoreLink href={namiHelpLineLink} />
            </Box>
          </Box>
          <Box className={classes.drawerDivider} />
          <Box className={classes.drawerItem}>
            <StarOutlineBlueIcon className={classes.drawerItemIcon} />
            <Box className={classes.drawerItemContent}>
              <Typography variant="body1" fontWeight={700}>{l10n.onboarding.tooYoung.trevorProject.title}</Typography>
              <Typography variant="body2">{l10n.onboarding.tooYoung.trevorProject.description}</Typography>
              <Typography variant="body2">{l10n.onboarding.tooYoung.trevorProject.availability}</Typography>
              <LearnMoreLink href={trevorProjectLink} />
            </Box>
          </Box>
          <Box className={classes.drawerDivider} />
          <Box className={classes.drawerItem}>
            <ShieldChildhelpIcon className={classes.drawerItemIcon} />
            <Box className={classes.drawerItemContent}>
              <Typography variant="body1" fontWeight={700}>{l10n.onboarding.tooYoung.childhelp.title}</Typography>
              <Typography variant="body2">{l10n.onboarding.tooYoung.childhelp.description}</Typography>
              <Typography variant="body2">{l10n.onboarding.tooYoung.childhelp.availability}</Typography>
              <LearnMoreLink href={childHelpHotlineLink} />
            </Box>
          </Box>
        </Box>
      </Drawer>
    </StepContainer>
  );
};

const useTooYoungStyles = makeStyles({ name: 'TooYoung' })(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    marginBottom: '0.5rem',
  },
  headerIcon: {
    width: '2.5rem',
    height: '2.5rem',
  },
  iconCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '3.5rem',
    height: '3.5rem',
    borderRadius: '50%',
    backgroundColor: 'rgba(57, 122, 74, 0.06)',
    flexShrink: 0,
  },
  rowIcon: {
    width: '2rem',
    height: '2rem',
    objectFit: 'contain' as const,
    display: 'block',
  },
  resourceList: {
    marginTop: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  resourceRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '0.5rem',
    cursor: 'pointer',
  },
  resourceText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    flex: 1,
  },
  resourceAction: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexShrink: 0,
    marginLeft: '1rem',
  },
  drawerPaper: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 420,
    },
  },
  drawerContent: {
    padding: '1.25rem',
    [theme.breakpoints.up('sm')]: {
      padding: '1.5rem',
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  drawerItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '1rem 0',
  },
  drawerItemIcon: {
    width: '2rem',
    height: '2rem',
    objectFit: 'contain' as const,
    flexShrink: 0,
  },
  drawerItemContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  drawerDivider: {
    borderTop: '1px solid rgba(0, 0, 0, 0.1)',
  },
  learnMoreLink: {
    textDecoration: 'none',
    marginTop: '0.25rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  resourceRowLink: {
    textDecoration: 'none',
    color: 'inherit',
  },
}));
